import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { CheckCircle as CheckIcon, Edit as EditIcon } from '@mui/icons-material';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUserProfile, UserProfileHook } from '../../hooks/useUserProfile';

export type OptionalProfileKey = 'basicInfo' | 'interests' | 'bio' | 'goals';
type GoalCategory = 'financial' | 'professional' | 'personal';

interface GoalRecord {
  id: string;
  category: GoalCategory;
  title: string;
}

const GOAL_CATEGORIES: Array<{ key: GoalCategory; label: string; placeholder: string }> = [
  {
    key: 'financial',
    label: 'Financial goal',
    placeholder: 'Raise new capital, grow revenue, improve margins…',
  },
  {
    key: 'professional',
    label: 'Professional goal',
    placeholder: 'Launch a product, expand team capabilities, win key accounts…',
  },
  {
    key: 'personal',
    label: 'Personal goal',
    placeholder: 'Improve wellbeing, deepen expertise, build relationships…',
  },
];

const EMPTY_GOAL_INPUTS: Record<GoalCategory, string> = {
  financial: '',
  professional: '',
  personal: '',
};

const INTEREST_CATEGORIES = [
  {
    title: 'Technology & Innovation',
    options: [
      'AI & Machine Learning',
      'Quantum Computing',
      'Cybersecurity',
      'Internet Infrastructure',
      'Semiconductors',
      'Web3 & Blockchain',
      'Biotech & Genomics',
      'Robotics',
      'Clean Tech',
    ],
  },
  {
    title: 'Geopolitics & Security',
    options: [
      'US Domestic Policy',
      'US National Security',
      'European Affairs',
      'Middle East',
      'Asia-Pacific',
      'Latin America',
      'Africa',
      'Diplomacy & Negotiation',
      'Sanctions & Export Controls',
    ],
  },
  {
    title: 'Economy & Markets',
    options: [
      'Macroeconomics',
      'Global Trade',
      'Supply Chains',
      'Capital Markets',
      'Private Equity',
      'Venture Capital',
      'Real Estate',
      'Fintech',
      'Renewable Energy Markets',
    ],
  },
  {
    title: 'Lifestyle & Personal Interests',
    options: [
      'Rock Climbing',
      'Mountain Biking',
      'Trail Running',
      'Backcountry Skiing',
      'Surfing',
      'Photography',
      'Music Production',
      'Culinary Arts',
      'Travel Planning',
    ],
  },
];

export interface OnboardingChecklistProps {
  className?: string;
  profileState?: UserProfileHook;
  onGoalsUpdated?: () => void;
}

export function OnboardingChecklist({ className, profileState, onGoalsUpdated }: OnboardingChecklistProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const supabase = useSupabaseClient();
  const { user, getEffectiveUserId } = useAuth();

  const fallbackProfileState = useUserProfile();
  const { profile, loading, updating, updateBasicInfo, updateOptionalFields } = profileState ?? fallbackProfileState;

  const [editor, setEditor] = useState<OptionalProfileKey | null>(null);
  const [basicInfo, setBasicInfo] = useState({ fullName: '', preferredEmail: '' });
  const [basicInfoTouched, setBasicInfoTouched] = useState(false);
  const [interests, setInterests] = useState<string[]>([]);
  const [newInterest, setNewInterest] = useState('');
  const [savingInterests, setSavingInterests] = useState(false);
  const [interestError, setInterestError] = useState<string | null>(null);
  const [bioValue, setBioValue] = useState('');
  const [goalInputs, setGoalInputs] = useState<Record<GoalCategory, string>>(EMPTY_GOAL_INPUTS);
  const [goalRecords, setGoalRecords] = useState<GoalRecord[]>([]);
  const [goalsLoading, setGoalsLoading] = useState(false);
  const [goalsSaving, setGoalsSaving] = useState(false);
  const [goalsError, setGoalsError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  useEffect(() => {
    if (!profile) return;
    setBasicInfo({
      fullName: profile.full_name ?? '',
      preferredEmail: profile.preferred_email ?? '',
    });
    setInterests(profile.interests ?? []);
    setBioValue(profile.bio ?? '');
  }, [profile]);

  const fetchGoals = useCallback(async () => {
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) {
      setGoalRecords([]);
      setGoalInputs(EMPTY_GOAL_INPUTS);
      return;
    }

    setGoalsLoading(true);
    setGoalsError(null);
    try {
      const { data, error } = await supabase
        .from('goals')
        .select('id, category, title')
        .eq('user_id', effectiveUserId)
        .order('category', { ascending: true });

      if (error) throw error;

      const records = (data ?? []) as GoalRecord[];
      setGoalRecords(records);
      const nextInputs: Record<GoalCategory, string> = { ...EMPTY_GOAL_INPUTS };
      records.forEach(goal => {
        nextInputs[goal.category] = goal.title ?? '';
      });
      setGoalInputs(nextInputs);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load goals.';
      setGoalsError(message);
    } finally {
      setGoalsLoading(false);
    }
  }, [supabase, getEffectiveUserId]);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  const goalsComplete = useMemo(
    () =>
      GOAL_CATEGORIES.every(({ key }) => {
        const record = goalRecords.find(goal => goal.category === key);
        return Boolean(record?.title && record.title.trim().length > 0);
      }),
    [goalRecords]
  );

  const completion = useMemo(
    () => ({
      basicInfo: Boolean(profile?.full_name && profile?.preferred_email),
      interests: Boolean(profile?.interests && profile.interests.length > 0),
      bio: Boolean(profile?.bio && profile.bio.trim().length > 0),
      goals: goalsComplete,
    }),
    [profile?.full_name, profile?.preferred_email, profile?.interests, profile?.bio, goalsComplete]
  );

  const allComplete = completion.basicInfo && completion.interests && completion.bio && completion.goals;
  const remaining = 4 - [completion.basicInfo, completion.interests, completion.bio, completion.goals].filter(Boolean).length;

  const openEditor = useCallback((key: OptionalProfileKey) => {
    setEditor(key);
    setFeedback(null);
    setSubmissionError(null);
  }, []);

  const closeEditor = useCallback(() => {
    setEditor(null);
  }, []);

  const handleGoalInputChange = useCallback((category: GoalCategory, value: string) => {
    setGoalInputs(prev => ({ ...prev, [category]: value }));
    setSubmissionError(null);
  }, []);

  const saveGoals = useCallback(async () => {
    if (!user) {
      setSubmissionError('You need to be signed in to update goals.');
      return;
    }

    setGoalsSaving(true);
    setSubmissionError(null);

    try {
      const trimmedInputs: Record<GoalCategory, string> = {
        financial: goalInputs.financial.trim(),
        professional: goalInputs.professional.trim(),
        personal: goalInputs.personal.trim(),
      };

      const effectiveUserId = getEffectiveUserId();
      if (!effectiveUserId) {
        throw new Error('User must be authenticated');
      }

      for (const { key } of GOAL_CATEGORIES) {
        const existing = goalRecords.find(goal => goal.category === key);
        const text = trimmedInputs[key];

        if (existing && !text) {
          const { error } = await supabase.from('goals').delete().eq('id', existing.id);
          if (error) throw error;
        } else if (existing && text && text !== existing.title) {
          const { error } = await supabase.from('goals').update({ title: text }).eq('id', existing.id);
          if (error) throw error;
        } else if (!existing && text) {
          const { error } = await supabase.from('goals').insert({
            title: text,
            category: key,
            user_id: effectiveUserId,
          });
          if (error) throw error;
        }
      }

      await fetchGoals();
      onGoalsUpdated?.();

      const savedTitles = GOAL_CATEGORIES.map(({ key }) => trimmedInputs[key]).filter(Boolean);
      await updateOptionalFields({ goals: savedTitles });

      setFeedback('Goals updated.');
      setEditor(null);
    } catch (error) {
      setSubmissionError(error instanceof Error ? error.message : 'Failed to update goals. Please try again.');
    } finally {
      setGoalsSaving(false);
    }
  }, [fetchGoals, goalInputs, goalRecords, onGoalsUpdated, supabase, updateOptionalFields, user, getEffectiveUserId]);

  const toggleInterest = useCallback((value: string) => {
    setInterests(prev => {
      const trimmed = value.trim();
      if (!trimmed) return prev;
      if (prev.some(item => item.toLowerCase() === trimmed.toLowerCase())) {
        return prev.filter(item => item.toLowerCase() !== trimmed.toLowerCase());
      }
      return [...prev, trimmed];
    });
    setInterestError(null);
  }, []);

  const handleNewInterestSubmit = useCallback(() => {
    const trimmed = newInterest.trim();
    if (!trimmed) {
      setInterestError('Enter an interest before adding.');
      return;
    }
    toggleInterest(trimmed);
    setNewInterest('');
  }, [newInterest, toggleInterest]);

  const handleBasicInfoChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setBasicInfo(prev => ({ ...prev, [name]: value }));
    setSubmissionError(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (!editor) return;

    setSubmissionError(null);
    setInterestError(null);

    if (editor === 'basicInfo') {
      setBasicInfoTouched(true);
      const trimmedName = basicInfo.fullName.trim();
      const trimmedEmail = basicInfo.preferredEmail.trim();
      
      if (!trimmedName || !trimmedEmail) {
        setSubmissionError('Both name and email are required.');
        return;
      }

      const result = await updateBasicInfo({
        fullName: trimmedName,
        preferredEmail: trimmedEmail,
      });
      
      if (result) {
        setFeedback('Basic information updated.');
        setEditor(null);
      } else {
        setSubmissionError('We could not update your basic information. Please try again.');
      }
      return;
    }

    if (editor === 'interests') {
      if (interests.length === 0) {
        setInterestError('Add at least one interest to continue.');
        return;
      }
      
      setSavingInterests(true);
      try {
        const trimmedInterests = Array.from(new Set(interests.map(value => value.trim()).filter(Boolean)));
        const result = await updateOptionalFields({ interests: trimmedInterests });
        if (!result) {
          setInterestError('We could not save your interests. Please try again.');
          return;
        }
        setInterests(result.interests ?? trimmedInterests);
        setFeedback('Interests updated.');
        setEditor(null);
      } catch (err) {
        setInterestError(err instanceof Error ? err.message : 'Failed to save interests.');
      } finally {
        setSavingInterests(false);
      }
      return;
    }

    if (editor === 'bio') {
      const result = await updateOptionalFields({ bio: bioValue.trim() ? bioValue.trim() : null });
      if (result) {
        setFeedback('Bio updated.');
        setEditor(null);
      } else {
        setSubmissionError('We could not update your bio. Please try again.');
      }
      return;
    }

    if (editor === 'goals') {
      await saveGoals();
    }
  }, [basicInfo, editor, interests, bioValue, saveGoals, updateBasicInfo, updateOptionalFields]);

  const items = useMemo(
    () => [
      {
        key: 'basicInfo' as OptionalProfileKey,
        label: 'Basic information',
        description: 'Tell us your name and preferred email.',
        completed: completion.basicInfo,
      },
      {
        key: 'interests' as OptionalProfileKey,
        label: 'Select interests',
        description: 'Choose what you want to track and learn about.',
        completed: completion.interests,
      },
      {
        key: 'bio' as OptionalProfileKey,
        label: 'Add bio',
        description: 'Share who you are and what you focus on.',
        completed: completion.bio,
      },
      {
        key: 'goals' as OptionalProfileKey,
        label: 'Define goals',
        description: 'Capture what success looks like for you.',
        completed: completion.goals,
      },
    ],
    [completion.basicInfo, completion.interests, completion.bio, completion.goals]
  );

  if (loading && !profile) {
    return null;
  }

  if (!profile) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      className={className}
      sx={{ border: '1px solid', borderColor: 'divider', p: 2, backgroundColor: 'background.paper' }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Complete your profile ({remaining === 0 ? 'all done' : `${remaining} step${remaining === 1 ? '' : 's'} left`})
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Finish these quick items to personalize recommendations.
          </Typography>
        </Box>
        <Chip color={allComplete ? 'success' : 'default'} icon={allComplete ? <CheckIcon /> : undefined} label={allComplete ? 'Ready to go' : 'Keep going'} />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Stack direction={isMobile ? 'column' : 'row'} spacing={1.5} alignItems={isMobile ? 'stretch' : 'center'}>
        {items.map(item => (
          <Button
            key={item.key}
            variant={item.completed ? 'outlined' : 'contained'}
            color={item.completed ? 'success' : 'primary'}
            startIcon={item.completed ? <CheckIcon /> : <EditIcon />}
            onClick={() => openEditor(item.key)}
            sx={{ flexGrow: 1, justifyContent: 'flex-start' }}
          >
            <Box sx={{ textAlign: 'left' }}>
              <Typography sx={{ fontWeight: 600 }}>{item.label}</Typography>
              <Typography variant="caption" display="block" color="text.secondary">
                {item.description}
              </Typography>
            </Box>
          </Button>
        ))}
      </Stack>

      {feedback && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setFeedback(null)}>
          {feedback}
        </Alert>
      )}

      <Dialog open={Boolean(editor)} onClose={closeEditor} maxWidth={editor === 'interests' ? 'md' : 'sm'} fullWidth>
        <DialogTitle>
          {editor === 'basicInfo' && 'Basic information'}
          {editor === 'interests' && 'Select your interests'}
          {editor === 'bio' && 'Add your bio'}
          {editor === 'goals' && 'Define your goals'}
        </DialogTitle>
        <DialogContent dividers>
          {editor === 'basicInfo' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Full name"
                name="fullName"
                value={basicInfo.fullName}
                onChange={handleBasicInfoChange}
                fullWidth
                autoComplete="name"
                error={basicInfoTouched && basicInfo.fullName.trim().length === 0}
                helperText={basicInfoTouched && basicInfo.fullName.trim().length === 0 ? 'Name is required.' : ''}
              />
              <TextField
                label="Preferred email"
                name="preferredEmail"
                value={basicInfo.preferredEmail}
                onChange={handleBasicInfoChange}
                fullWidth
                autoComplete="email"
                error={basicInfoTouched && basicInfo.preferredEmail.trim().length === 0}
                helperText={basicInfoTouched && basicInfo.preferredEmail.trim().length === 0 ? 'Email is required.' : ''}
              />
              <Typography variant="body2" color="text.secondary">
                We use this information to personalize your workspace experience and notify you of important activity.
              </Typography>
            </Box>
          )}
          {editor === 'interests' && (
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Select from our quick list or add your own focus areas. We use this to tailor updates for you.
              </Typography>
              <Stack spacing={2}>
                {INTEREST_CATEGORIES.map(category => (
                  <Box key={category.title}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                      {category.title}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {category.options.map(option => {
                        const selected = interests.some(item => item.toLowerCase() === option.toLowerCase());
                        return (
                          <Chip
                            key={option}
                            label={option}
                            color={selected ? 'primary' : 'default'}
                            variant={selected ? 'filled' : 'outlined'}
                            onClick={() => toggleInterest(option)}
                            sx={{ mb: 1 }}
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                ))}
              </Stack>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  label="Add custom interest"
                  value={newInterest}
                  onChange={event => {
                    setNewInterest(event.target.value);
                    setInterestError(null);
                  }}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleNewInterestSubmit();
                    }
                  }}
                  sx={{ flexGrow: 1, minWidth: 220 }}
                />
                <Button variant="outlined" onClick={handleNewInterestSubmit}>
                  Add
                </Button>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {interests.map(value => (
                  <Chip
                    key={value}
                    label={value}
                    color="primary"
                    onDelete={() => toggleInterest(value)}
                    sx={{ mb: 1 }}
                  />
                ))}
              </Stack>
              {interestError && (
                <Alert severity="error">{interestError}</Alert>
              )}
            </Box>
          )}
          {editor === 'bio' && (
            <TextField
              label="Bio"
              value={bioValue}
              onChange={event => setBioValue(event.target.value)}
              fullWidth
              multiline
              minRows={4}
              helperText="A short description helps others understand your work."
            />
          )}
          {editor === 'goals' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {goalsError && (
                <Alert severity="error">
                  {goalsError}
                </Alert>
              )}
              {goalsLoading && goalRecords.length === 0 ? (
                <Box sx={{ py: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                GOAL_CATEGORIES.map(({ key, label, placeholder }) => (
                  <Box key={key}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {label}
                    </Typography>
                    <TextField
                      value={goalInputs[key]}
                      onChange={event => handleGoalInputChange(key, event.target.value)}
                      placeholder={placeholder}
                      fullWidth
                      multiline
                      minRows={2}
                    />
                  </Box>
                ))
              )}
            </Box>
          )}
          {(submissionError || (editor === 'interests' && interestError)) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {submissionError || interestError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor} disabled={updating || goalsSaving || savingInterests}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={updating || (editor === 'goals' && goalsSaving) || (editor === 'interests' && savingInterests)}
          >
            {editor === 'goals' && goalsSaving ? 'Saving…' : editor === 'interests' && savingInterests ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
